import Queue from 'utils/queue';
import stringMemory from 'utils/string-memory';

export interface Product {
  handle: string;
  url: string;
  sku: string;
  color: string;
  colorLabel: string;
  image: string;
}

class LinkedProducts {
  private items: { [key: string]: { products: Product[] } };

  private queue: Queue;

  constructor() {
    this.items = null;
    this.queue = new Queue();
  }

  static async getAllProducts() {
    const url = new URL(theme.routes.allProductsCollection, window.origin);

    let currentPage = 1;
    let allProducts: Product[] = [];
    let hasMorePages = true;

    while (hasMorePages) {
      const params = new URLSearchParams({
        view: 'linked',
        page: currentPage.toString(),
      });

      url.search = params.toString();

      try {
        const response = await fetch(url);
        const pageProducts: Product[] = await response.json();

        allProducts = allProducts.concat(pageProducts);

        if (currentPage == theme.pageCount) {
          hasMorePages = false;
        } else {
          currentPage += 1;
        }
      } catch (error) {
        throw new Error(`Failed to fetch products on page ${currentPage}. ${error}`);
      }
    }

    return allProducts;
  }

  static groupItems(products: Product[]) {
    const groupedItems = products.reduce((acc, currentItem) => {
      const { sku, handle } = currentItem;

      if (!acc[sku]) {
        acc[sku] = {
          handle,
          products: [],
        };
      }

      acc[sku].products.push(currentItem);

      return acc;
    }, {});

    return groupedItems;
  }

  async init() {
    const memory = stringMemory(sessionStorage);

    const response = await this.queue.add(() =>
      memory.get(`linked-products`, async () => {
        const products = await LinkedProducts.getAllProducts();
        return JSON.stringify(products);
      }),
    );

    this.items = LinkedProducts.groupItems(JSON.parse(response));
  }

  async getRelated(sku: string) {
    await this.init();

    // Slice to match global product number metafield value
    const match = this.items[sku.slice(0, 9)];

    if (!match) {
      return null;
    }

    return match.products;
  }
}

export default LinkedProducts;
